import { inject } from "@angular/core";
import { signalStore, withState, withMethods, patchState } from "@ngrx/signals";
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from "@application/data-access";
import { ErrorStore } from "@application/errors/data-access";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { pipe, switchMap, tap } from "rxjs";
import { tapResponse } from "@ngrx/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { SettingsPostsService } from "../infra/posts.service";
import { IPost } from "../entities/posts.model";

export interface ISettingsPosts {
    showViewModal: boolean;
    showEditModal: boolean;
    post: IPost;
    postsToDisplay: IPost[];
    currentPostIndex: number;
    isAutoDisplayMode: boolean;
}

export const SettingsPostsInitialStore: ISettingsPosts = {
    showViewModal: false,
    showEditModal: false,
    post: null,
    postsToDisplay: [],
    currentPostIndex: 0,
    isAutoDisplayMode: false
};

export const SettingsPostsStore = signalStore(
    { providedIn: 'root' },
    withState(SettingsPostsInitialStore),
    withMethods((
        store,
        postsService = inject(SettingsPostsService),
        errorStore = inject(ErrorStore)) => {
        return {
            toggleShowViewModal: (showViewModal = !store.showViewModal()) => {
                patchState(store, { showViewModal, isAutoDisplayMode: false });
            },

            toggleShowEditModal: (showEditModal = !store.showEditModal()) => {
                patchState(store, { showEditModal });
            },

            getPost: rxMethod<string>(pipe(
            tap(() => patchState(store, setPropLoading('getPost'))),
            switchMap((payload) => postsService.getPost(payload).pipe(
                tapResponse({
                next: (post) => {
                    patchState(store, { post, ...setPropLoaded('getPost') })
                },
                error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(store, setPropError('getPost', error));
                }
                })
            )))),

            removePostagem: rxMethod<string>(pipe(
                tap(() => patchState(store, setPropLoading('removePostagem'))),
                switchMap((postId) => postsService.deletePostagem(postId).pipe(
                    tapResponse({
                        next: () => {
                            patchState(store, setPropLoaded('removePostagem'))
                        },
                        error: (error: HttpErrorResponse) => {
                            errorStore.handleError({ error, title: 'SETTINGS.POSTS.FORM.ALERT.DELETE.ERROR_TITLE' });
                            patchState(store, setPropError('removePostagem', error));
                        }
                    })
                ))
            )),

            blankPostState: () => {
                patchState(store, { post: null }, setPropInit('getPost'));
            },

            setPropsInit(props: string[]): void {
                patchState(store, { ...setPropInit(...props) });
            },

            markPostAsViewed(): void {
                const currentIndex = store.currentPostIndex();
                const nextIndex = currentIndex + 1;
                const postsToDisplay = store.postsToDisplay();
                
                if (nextIndex < postsToDisplay.length) {
                    patchState(store, { 
                        currentPostIndex: nextIndex,
                        post: postsToDisplay[nextIndex],
                        showViewModal: true,
                        isAutoDisplayMode: true
                    });
                } else {
                    patchState(store, { showViewModal: false, post: null, isAutoDisplayMode: false });
                }
            }
        }
    }),
    withReqState('getPost', 'removePostagem'),
);
